import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'react-toastify';
import Button from './Button';

interface TaskSummary {
  id: string;
  name: string;
  description: string;
  readOnly: boolean;
  danger?: string;
}

interface PlannedChange {
  collection: string;
  documentId: string;
  before: Record<string, unknown>;
  after: Record<string, unknown>;
}

interface TaskResult {
  taskId: string;
  dryRun: boolean;
  scanned: number;
  affected: number;
  applied: number;
  sample: PlannedChange[];
  notes?: string[];
  errors?: string[];
}

const formatValue = (value: unknown): string => {
  if (value === undefined) return '—';
  if (value === null) return 'null';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const MaintenanceTasksPanel: React.FC = () => {
  const [tasks, setTasks] = useState<TaskSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [runningTask, setRunningTask] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, TaskResult>>({});

  const fetchTasks = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const data = await api.get<{ tasks: TaskSummary[] }>('/admin/tasks');
      setTasks(data.tasks);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load maintenance tasks';
      setLoadError(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const runTask = async (task: TaskSummary, dryRun: boolean) => {
    if (!dryRun) {
      const confirmation = task.danger
        ? `${task.danger}\n\nApply "${task.name}" for real? This will modify data.`
        : `Apply "${task.name}" for real? This will modify data.`;

      if (!window.confirm(confirmation)) {
        return;
      }
    }

    setRunningTask(task.id);
    try {
      const result = await api.post<TaskResult>(`/admin/tasks/${task.id}/run`, { dryRun });
      setResults(prev => ({ ...prev, [task.id]: result }));

      if (dryRun) {
        toast.info(`Dry run complete: ${result.affected} of ${result.scanned} affected`);
      } else if (result.errors?.length) {
        toast.warning(`Applied with ${result.errors.length} error(s)`);
      } else {
        toast.success(`Applied: ${result.applied} document(s) changed`);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Task failed';
      toast.error(message);
    } finally {
      setRunningTask(null);
    }
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading tasks...</div>;
  }

  if (loadError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4" role="alert">
        <p className="text-red-800 font-medium">Could not load maintenance tasks</p>
        <p className="text-red-700 text-sm mt-1">{loadError}</p>
        <Button variant="secondary" onClick={fetchTasks} className="mt-3">
          Retry
        </Button>
      </div>
    );
  }

  if (tasks.length === 0) {
    return <p className="text-gray-600 py-8">No maintenance tasks are registered.</p>;
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-600">
        Every task supports a dry run that reports exactly what would change without writing
        anything. Apply stays disabled until you have run one.
      </p>

      {tasks.map(task => {
        const result = results[task.id];
        const hasDryRun = Boolean(result?.dryRun);
        const isRunning = runningTask === task.id;

        return (
          <div key={task.id} className="border border-gray-200 rounded-lg p-5 bg-white">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-[16rem]">
                <h3 className="text-lg font-semibold text-gray-900">{task.name}</h3>
                <p className="text-sm text-gray-600 mt-1">{task.description}</p>
                {task.readOnly && (
                  <span className="inline-block mt-2 text-xs font-medium px-2 py-1 rounded bg-gray-100 text-gray-700">
                    Read-only
                  </span>
                )}
              </div>

              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  onClick={() => runTask(task, true)}
                  disabled={isRunning}
                  isLoading={isRunning}
                >
                  Dry run
                </Button>
                {!task.readOnly && (
                  <Button
                    variant="primary"
                    onClick={() => runTask(task, false)}
                    disabled={isRunning || !hasDryRun || result?.affected === 0}
                    title={
                      !hasDryRun
                        ? 'Run a dry run first'
                        : result?.affected === 0
                          ? 'Nothing to apply'
                          : undefined
                    }
                  >
                    Apply
                  </Button>
                )}
              </div>
            </div>

            {task.danger && (
              <p className="mt-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                {task.danger}
              </p>
            )}

            {result && (
              <div className="mt-4 border-t border-gray-100 pt-4">
                <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
                  <span className="font-medium text-gray-900">
                    {result.dryRun ? 'Dry run' : 'Applied'}
                  </span>
                  <span className="text-gray-600">Scanned: {result.scanned}</span>
                  <span className="text-gray-600">Affected: {result.affected}</span>
                  {!result.dryRun && (
                    <span className="text-gray-600">Changed: {result.applied}</span>
                  )}
                </div>

                {result.notes && result.notes.length > 0 && (
                  <ul className="mt-3 text-sm text-gray-700 list-disc list-inside space-y-1">
                    {result.notes.map((note, i) => (
                      <li key={i}>{note}</li>
                    ))}
                  </ul>
                )}

                {result.errors && result.errors.length > 0 && (
                  <div className="mt-3 bg-red-50 border border-red-200 rounded p-3">
                    <p className="text-sm font-medium text-red-900">Errors</p>
                    <ul className="mt-1 text-sm text-red-700 list-disc list-inside space-y-1">
                      {result.errors.map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {result.sample.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-gray-900 mb-2">
                      Preview ({result.sample.length} of {result.affected})
                    </p>
                    <div className="overflow-x-auto border border-gray-200 rounded">
                      <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium text-gray-500">
                              Collection
                            </th>
                            <th className="px-3 py-2 text-left font-medium text-gray-500">ID</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-500">Before</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-500">After</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {result.sample.map(change => (
                            <tr key={`${change.collection}-${change.documentId}`}>
                              <td className="px-3 py-2 text-gray-700">{change.collection}</td>
                              <td className="px-3 py-2 font-mono text-xs text-gray-600">
                                {change.documentId}
                              </td>
                              <td className="px-3 py-2 text-gray-600">
                                {Object.entries(change.before).map(([key, value]) => (
                                  <div key={key}>
                                    <span className="text-gray-400">{key}:</span>{' '}
                                    {formatValue(value)}
                                  </div>
                                ))}
                              </td>
                              <td className="px-3 py-2 text-gray-900">
                                {Object.entries(change.after).map(([key, value]) => (
                                  <div key={key}>
                                    <span className="text-gray-400">{key}:</span>{' '}
                                    {formatValue(value)}
                                  </div>
                                ))}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default MaintenanceTasksPanel;
